import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ShieldAlert, Clock, AlertTriangle, ArrowRight, Sparkles } from "lucide-react";
import { SkillRadar } from "./SkillRadar";
import { CostCompareCard } from "./CostCompareCard";

const STATUS_TONE = {
  "acilacak": "border-rose-500/40 text-rose-700 bg-rose-50",
  "dolu-riskli": "border-rose-500/40 text-rose-700 bg-rose-50",
  "ic-ilan-acildi": "border-sky-500/40 text-sky-700 bg-sky-50",
  "izleme": "border-amber-500/40 text-amber-700 bg-amber-50",
  "dolu-stabil": "border-emerald-500/40 text-emerald-700 bg-emerald-50",
};
const CRITICALITY_LABEL = {
  "cok-yuksek": { label: "Çok Yüksek Kritiklik", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  "yuksek": { label: "Yüksek Kritiklik", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  "orta": { label: "Orta Kritiklik", cls: "bg-sky-100 text-sky-800 border-sky-300" },
};

export const CriticalRoleDrawer = ({ critId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!critId || !open) return;
    setLoading(true);
    setSelectedCandidate(null);
    api.get(`/critical-roles/${critId}/detail`)
      .then((r) => {
        setData(r.data);
        if (r.data.candidates?.length) setSelectedCandidate(r.data.candidates[0]);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [critId, open]);

  const crit = CRITICALITY_LABEL[data?.criticality] || CRITICALITY_LABEL.orta;
  const statusCls = STATUS_TONE[data?.status] || "border-slate-300 text-slate-700 bg-slate-50";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-3xl overflow-y-auto bg-slate-50 p-0">
        {loading || !data ? (
          <div className="p-8 text-sm text-slate-500">Yükleniyor…</div>
        ) : (
          <div className="p-6 space-y-5">
            <SheetHeader>
              <div className="flex items-start justify-between gap-3 mb-2">
                <Badge variant="outline" className={`${statusCls} text-[0.65rem] font-semibold`} data-testid="crit-status-badge">
                  {data.status_label}
                </Badge>
                <Badge variant="outline" className={`${crit.cls} text-[0.65rem] font-semibold`} data-testid="crit-criticality-badge">
                  {crit.label}
                </Badge>
              </div>
              <SheetTitle className="font-display text-2xl text-slate-900 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-600" />
                {data.role_title}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-600">
                {data.department} · {data.career_band}
              </SheetDescription>
            </SheetHeader>

            {/* Rationale */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="label-eyebrow mb-1">Neden Kritik?</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{data.rationale}</p>
                  {data.target_fill_months != null && (
                    <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Hedef doldurma süresi: <span className="text-slate-800 font-semibold">{data.target_fill_months} ay</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Candidates rank */}
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="candidates-rank">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow">Yedekleme Adayları</div>
                <span className="text-xs font-mono text-slate-500">{data.candidates.length} aday</span>
              </div>
              <div className="space-y-2">
                {data.candidates.map((c, idx) => (
                  <button
                    key={c.employee_id}
                    onClick={() => setSelectedCandidate(c)}
                    data-testid={`candidate-row-${c.employee_id}`}
                    className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border transition ${
                      selectedCandidate?.employee_id === c.employee_id
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <span className="font-mono text-sm text-slate-400 w-5">{idx + 1}</span>
                    <Avatar className="w-9 h-9"><AvatarImage src={c.avatar} /><AvatarFallback>{c.name?.charAt(0)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                      <div className="text-[0.7rem] text-slate-500 truncate">{c.title}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-mono text-sm font-semibold text-sky-700">%{c.readiness_score}</div>
                        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">hazır</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm font-semibold text-amber-700">{c.time_to_ready_months}</div>
                        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">ay</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selected candidate radar */}
            {selectedCandidate && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="label-eyebrow">Aday Detayı</span>
                  <ArrowRight className="w-3 h-3 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-900">{selectedCandidate.name}</span>
                </div>
                <SkillRadar employeeId={selectedCandidate.employee_id} targetRoleId={data.role_id} />
                {selectedCandidate.key_gaps?.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="label-eyebrow mb-2 text-amber-700">Geliştirilecek Alanlar</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedCandidate.key_gaps.map((g) => (
                        <span key={g} className="px-2.5 py-0.5 rounded-full text-xs border border-amber-300 bg-amber-50 text-amber-800">{g}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pre-baked AI narrative */}
            {data.narrative && (
              <div className="rounded-2xl border border-sky-300 bg-gradient-to-br from-sky-50 to-white p-5" data-testid="narrative-card">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-sky-600" />
                  <span className="label-eyebrow text-sky-700">AI Yorumu</span>
                </div>
                <div className="font-display text-lg font-semibold text-slate-900 mb-2">{data.narrative.baslik}</div>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{data.narrative.ozet_yorumu}</p>
                {data.narrative.aday_yorumlari?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {data.narrative.aday_yorumlari.map((c, i) => (
                      <div key={i} className="text-xs leading-relaxed text-slate-700 border-l-2 border-sky-300 pl-3">
                        {c.yorum}
                      </div>
                    ))}
                  </div>
                )}
                {data.narrative.kapanis_yorumu && (
                  <div className="text-sm font-medium text-slate-900 border-t border-sky-200 pt-3">
                    {data.narrative.kapanis_yorumu}
                  </div>
                )}
              </div>
            )}

            {/* Cost compare */}
            <CostCompareCard data={data.cost_compare} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CriticalRoleDrawer;
